import axios from 'axios';
import { sharedGet, invalidateSharedGet } from '@/lib/sharedGet';
import { formatDate } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

let _docsAbort = null;              // 進行中の一覧・検索（新しい検索が来たら打ち切る）

export const dmsApi = {
  // D9 — ヘッダーと画面が同じ GET を同時に出していた（開発モードでは2倍）。
  meta: () => sharedGet(`${API}/meta`),
  features: () => axios.get(`${API}/features`).then((r) => r.data),
  toggleFeature: (fid, enabled) => axios.post(`${API}/features/${fid}/toggle`, { enabled }).then((r) => r.data),
  folders: () => sharedGet(`${API}/folders`),
  createFolder: (b) => axios.post(`${API}/folders`, b).then((r) => { invalidateSharedGet('/folders'); return r.data; }),
  updateFolder: (fid, b) => axios.put(`${API}/folders/${fid}`, b).then((r) => { invalidateSharedGet('/folders'); return r.data; }),
  uploadCheckBatch: (files) => axios.post(`${API}/documents/upload-check-batch`, { files }).then((r) => r.data),
  bulkAction: (body) => axios.post(`${API}/documents/bulk`, body)
    .then((r) => { invalidateSharedGet('/documents'); return r.data; }),
  logDuplicateAck: (reasons) => axios.post(`${API}/documents/duplicate-ack`, { reasons }).then((r) => r.data),
  deleteFolder: (fid, cascade = false) => axios.delete(`${API}/folders/${fid}`,
    { params: cascade ? { cascade: true } : {} })
    .then((r) => { invalidateSharedGet('/folders'); return r.data; }),
  folderDeletePreview: (fid) => axios.get(`${API}/folders/${fid}/delete-preview`).then((r) => r.data),
  tags: () => axios.get(`${API}/tags`).then((r) => r.data),
  createTag: (name) => axios.post(`${API}/tags`, { name }).then((r) => r.data),
  deleteTag: (tid) => axios.delete(`${API}/tags/${tid}`).then((r) => r.data),
  // L4 — 同じ一覧を複数の部品が同時に取りに行っていた（開発モードでは2倍）。
  // 進行中の取得には相乗りする。一覧は中身が変わるので使い回しはしない（ttl 0）。
  documents: (params, config) => sharedGet(`${API}/documents`, { params, ttl: 0, ...(config || {}) }),
  // 検索は打つたびに条件が変わる。前の取得は打ち切る（古い結果で上書きしない・
  // 重い検索を並べて走らせない）。
  documentsLatest: (params) => {
    _docsAbort?.abort();
    _docsAbort = new AbortController();
    return dmsApi.documents(params, { signal: _docsAbort.signal });
  },
  isCanceled: (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError'
    || e?.name === 'AbortError',
  document: (did) => axios.get(`${API}/documents/${did}`).then((r) => r.data),
  upload: (formData) => axios.post(`${API}/documents/upload`, formData).then((r) => r.data),
  updateDocument: (did, b) => axios.put(`${API}/documents/${did}`, b).then((r) => r.data),
  replaceFile: (did, formData) => axios.post(`${API}/documents/${did}/replace`, formData).then((r) => r.data),
  deleteDocument: (did) => axios.delete(`${API}/documents/${did}`).then((r) => r.data),
  restoreDocument: (did) => axios.post(`${API}/documents/${did}/restore`).then((r) => r.data),
  purgeExpired: () => axios.post(`${API}/trash/purge-expired`).then((r) => r.data),
  addLink: (did, b) => axios.post(`${API}/documents/${did}/links`, b).then((r) => r.data),
  removeLink: (did, lid) => axios.delete(`${API}/documents/${did}/links/${lid}`).then((r) => r.data),
  projectsLookup: (q) => axios.get(`${API}/projects-lookup`, { params: q ? { q } : {} }).then((r) => r.data),
  settings: () => axios.get(`${API}/settings`).then((r) => r.data),
  saveSettings: (b) => axios.put(`${API}/settings`, b).then((r) => r.data),
  navVisibility: () => axios.get(`${API}/nav-visibility`).then((r) => r.data),
  saveNavVisibility: (visibility) => axios.put(`${API}/nav-visibility`, { visibility }).then((r) => r.data),
  health: () => axios.get(`${API}/admin/health`).then((r) => r.data),
  // D1/D2/D3 — 取り込み前の照合と種類の確認
  uploadCheck: (b) => axios.post(`${API}/documents/upload-check`, b).then((r) => r.data),
  typeReview: () => axios.get(`${API}/documents/type-review`).then((r) => r.data),
  setDocType: (did, docType) => axios.post(`${API}/documents/${did}/type`, { doc_type: docType }).then((r) => r.data),
  // D4 — 読取の待ち行列
  ocrBacklog: () => axios.get(`${API}/ocr/backlog`).then((r) => r.data),
  ocrRequeue: (b) => axios.post(`${API}/ocr/requeue`, b || {}).then((r) => r.data),
  // D5 — 迷子の書類
  orphans: () => axios.get(`${API}/admin/orphans`).then((r) => r.data),
  organizeOrphans: (b) => axios.post(`${API}/admin/orphans/organize`, b || {}).then((r) => r.data),
  // D6b/D6c — 索引と意味検索
  indexHealth: () => axios.get(`${API}/admin/index-health`).then((r) => r.data),
  indexPrune: () => axios.post(`${API}/admin/index-prune`).then((r) => r.data),
  semanticStatus: () => axios.get(`${API}/admin/semantic-status`).then((r) => r.data),
  semanticBuild: () => axios.post(`${API}/admin/semantic-build`).then((r) => r.data),
  folderRestructureDryRun: () => axios.get(`${API}/admin/folder-restructure-dryrun`).then((r) => r.data),
  // D7 — まとめて片付ける
  bulk: (b) => axios.post(`${API}/documents/bulk`, b).then((r) => r.data),
  adoptionStatus: () => axios.get(`${API}/adoption/status`).then((r) => r.data),
  adoptionRun: (dryRun) => axios.post(`${API}/adoption/run`, {}, { params: { dry_run: dryRun } }).then((r) => r.data),
  // Phase 2 — trays / stamps / comments
  trays: () => axios.get(`${API}/trays`).then((r) => r.data),
  stamp: (did, stamp, note) => axios.post(`${API}/documents/${did}/stamp`, { stamp, note: note || null }).then((r) => r.data),
  // 1 · 向きの保存（原本は書き換えない・2026-11-05）
  rotate: (did, page, delta) => axios.post(`${API}/documents/${did}/rotation`,
    { page: page || 1, delta }).then((r) => r.data),
  resetRotation: (did) => axios.delete(`${API}/documents/${did}/rotation`).then((r) => r.data),
  addComment: (did, body, pageNo) => axios.post(`${API}/documents/${did}/comments`, { body, page_no: pageNo || null }).then((r) => r.data),
  deleteComment: (did, cid) => axios.delete(`${API}/documents/${did}/comments/${cid}`).then((r) => r.data),
  // Phase 3 — requests / reminders / workflow rules
  requests: (status = 'open') => axios.get(`${API}/requests`, { params: { status } }).then((r) => r.data),
  createRequest: (b) => axios.post(`${API}/requests`, b).then((r) => r.data),
  fulfillRequest: (rid, formData) => axios.post(`${API}/requests/${rid}/fulfill`, formData).then((r) => r.data),
  cancelRequest: (rid) => axios.post(`${API}/requests/${rid}/cancel`).then((r) => r.data),
  reminders: () => sharedGet(`${API}/reminders`),
  workflowRules: () => axios.get(`${API}/workflow-rules`).then((r) => r.data),
  createRule: (b) => axios.post(`${API}/workflow-rules`, b).then((r) => r.data),
  updateRule: (rid, b) => axios.put(`${API}/workflow-rules/${rid}`, b).then((r) => r.data),
  deleteRule: (rid) => axios.delete(`${API}/workflow-rules/${rid}`).then((r) => r.data),
  // Phase 4 — record linking
  linked: (ttype, tid) => axios.get(`${API}/linked/${ttype}/${tid}`).then((r) => r.data),
  // Code128 ラベルは識別子そのものが載る（URLではない）ので、識別子から資産を引く。
  resolveLabelCode: (code) => axios.get(`${API}/resolve-label-code`, { params: { code } }).then((r) => r.data),
  linkedLookup: (ttype, tid, q) => axios.get(`${API}/linked/${ttype}/${tid}/lookup`,
    { params: typeof q === 'string' ? (q ? { q } : {}) : (q || {}) }).then((r) => r.data),
  attachLinked: (ttype, tid, documentId) => axios.post(`${API}/linked/${ttype}/${tid}/attach`, { document_id: documentId }).then((r) => r.data),
  uploadLinked: (ttype, tid, formData) => axios.post(`${API}/linked/${ttype}/${tid}/upload`, formData).then((r) => r.data),
  unlinkLinked: (ttype, tid, linkId) => axios.delete(`${API}/linked/${ttype}/${tid}/${linkId}`).then((r) => r.data),
  // Bundles / split
  bundles: () => axios.get(`${API}/bundles`).then((r) => r.data),
  bundle: (bid) => axios.get(`${API}/bundles/${bid}`).then((r) => r.data),
  createBundle: (b) => axios.post(`${API}/bundles`, b).then((r) => r.data),
  updateBundle: (bid, b) => axios.put(`${API}/bundles/${bid}`, b).then((r) => r.data),
  deleteBundle: (bid) => axios.delete(`${API}/bundles/${bid}`).then((r) => r.data),
  splitDocument: (did, parts) => axios.post(`${API}/documents/${did}/split`, { parts }).then((r) => r.data),
  mergeDocuments: (title, documentIds) => axios.post(`${API}/documents/merge`, { title, document_ids: documentIds }).then((r) => r.data),
  pageThumbs: (did) => axios.get(`${API}/documents/${did}/page-thumbs`).then((r) => r.data),
  reorderPages: (did, order) => axios.post(`${API}/documents/${did}/reorder-pages`, { order }).then((r) => r.data),
  createBundleShare: (bid, days = 7) => axios.post(`${API}/bundles/${bid}/share`, { days }).then((r) => r.data),
  revokeBundleShare: (bid) => axios.delete(`${API}/bundles/${bid}/share`).then((r) => r.data),
  // OCR
  rebuildKeywords: (did) => axios.post(`${API}/documents/${did}/keywords`).then((r) => r.data),
  aiTags: (did) => axios.post(`${API}/documents/${did}/ai-tags`).then((r) => r.data),
  aiTagsStatus: (did) => axios.get(`${API}/documents/${did}/ai-tags`).then((r) => r.data),
  runOcr: (did) => axios.post(`${API}/documents/${did}/ocr`).then((r) => r.data),
  ocrInfo: (did, full) => axios.get(`${API}/documents/${did}/ocr`,
    { params: full ? { full: true } : {} }).then((r) => r.data),
  // L8 — どの書類でも読み直せる（文字だけ / AIで）。前の結果は履歴から戻せる。
  reread: (did, mode, overwriteManual) => axios.post(`${API}/documents/${did}/reread`,
    { mode, overwrite_manual: !!overwriteManual }).then((r) => r.data),
  rereadState: (did) => axios.get(`${API}/documents/${did}/reread`).then((r) => r.data),
  rereadRestore: (did, snapshotId) => axios.post(`${API}/documents/${did}/reread/restore`,
    { snapshot_id: snapshotId }).then((r) => r.data),
  // L9 — 読み取った本文・図番・改訂・責任者・キーワードを人が直す
  readFields: (did) => axios.get(`${API}/documents/${did}/read-fields`).then((r) => r.data),
  saveReadFields: (did, b) => axios.put(`${API}/documents/${did}/read-fields`, b).then((r) => r.data),
  askDocument: (did, question) => axios.post(`${API}/documents/${did}/ask`, { question }).then((r) => r.data),
  suggestFiling: (did) => axios.post(`${API}/documents/${did}/suggest-filing`).then((r) => r.data),
  // V4 — intelligent ingestion (drop → OCR → AI understanding → review)
  ingestCreateBatch: (folderId, expectedCount) => axios.post(`${API}/ingest/batches`,
    { folder_id: folderId, expected_count: expectedCount || 0 }).then((r) => r.data),
  ingestFile: (bid, formData) => axios.post(`${API}/ingest/batches/${bid}/files`, formData).then((r) => r.data),
  ingestBatch: (bid) => axios.get(`${API}/ingest/batches/${bid}`).then((r) => r.data),
  ingestQueue: () => axios.get(`${API}/ingest/queue`).then((r) => r.data),
  ingestQueueAdmin: () => axios.get(`${API}/ingest/queue/admin`).then((r) => r.data),
  ingestQueueCancel: (jobId) => axios.post(`${API}/ingest/queue/${jobId}/cancel`).then((r) => r.data),
  ingestQueueCancelAll: (body) => axios.post(`${API}/ingest/queue/cancel-all`, body || {}).then((r) => r.data),
  ingestActive: () => axios.get(`${API}/ingest/batches`).then((r) => r.data),
  ingestOpen: (userId) => axios.get(`${API}/ingest/open`,
    { params: userId ? { user_id: userId } : {} }).then((r) => r.data),
  ingestApply: (iid, body) => axios.post(`${API}/ingest/items/${iid}/apply`, body).then((r) => r.data),
  ingestApplyConfident: (bid) => axios.post(`${API}/ingest/batches/${bid}/apply-confident`).then((r) => r.data),
  ingestUndo: (bid) => axios.post(`${API}/ingest/batches/${bid}/undo`).then((r) => r.data),
  // V4 — desk surface (pinned / recent folders, thumbnails, index diagnostics)
  prefs: () => sharedGet(`${API}/prefs`),
  togglePin: (fid) => axios.post(`${API}/folders/${fid}/pin`).then((r) => { invalidateSharedGet('/prefs'); return r.data; }),
  visitFolder: (fid) => axios.post(`${API}/folders/${fid}/visit`).then((r) => { invalidateSharedGet('/prefs'); return r.data; }),
  thumbUrl: async (did) => {
    const r = await axios.get(`${API}/documents/${did}/thumb`, { responseType: 'blob' });
    if (r.status === 204 || !r.data || r.data.size === 0) return null;
    return URL.createObjectURL(r.data);
  },
  searchIndexStats: () => axios.get(`${API}/search-index/stats`).then((r) => r.data),
  ingestRetry: (iid, processingMode) => axios.post(`${API}/ingest/items/${iid}/retry`,
    processingMode ? { processing_mode: processingMode } : {}).then((r) => r.data),
  ingestSkip: (iid) => axios.post(`${API}/ingest/items/${iid}/skip`).then((r) => r.data),
  ingestRetryFailed: (bid, processingMode) => axios.post(
    `${API}/ingest/batches/${bid}/retry-failed`,
    processingMode ? { processing_mode: processingMode } : {}).then((r) => r.data),
  autoGroupStart: (body) => axios.post(`${API}/bundles/auto-group`, body).then((r) => r.data),
  autoGroupStatus: (jid) => axios.get(`${API}/bundles/auto-group/${jid}`).then((r) => r.data),
  autoGroupCreate: (jid, groupIds) => axios.post(`${API}/bundles/auto-group/${jid}/create`,
    { group_ids: groupIds }).then((r) => r.data),
  ingestClose: (bid) => axios.post(`${API}/ingest/batches/${bid}/close`).then((r) => r.data),
  // V4 — admin bulk folder maintenance
  bulkScope: (folderId, includeSub) => axios.get(`${API}/ingest/bulk/scope`,
    { params: { ...(folderId ? { folder_id: folderId } : {}), include_subfolders: includeSub } }).then((r) => r.data),
  bulkStart: (body) => axios.post(`${API}/ingest/bulk`, body).then((r) => r.data),
  bulkJobs: () => axios.get(`${API}/ingest/bulk/jobs`).then((r) => r.data),
  bulkJob: (jid) => axios.get(`${API}/ingest/bulk/jobs/${jid}`).then((r) => r.data),
  bulkControl: (jid, action) => axios.post(`${API}/ingest/bulk/jobs/${jid}/${action}`).then((r) => r.data),
  askScope: (question, scopeType, scopeId) => axios.post(`${API}/ask-scope`, { question, scope_type: scopeType, scope_id: scopeId }).then((r) => r.data),
  fileBlobUrl: async (did, versionId) => {
    const r = await axios.get(`${API}/documents/${did}/file`,
      { params: versionId ? { version_id: versionId } : {}, responseType: 'blob' });
    return { url: URL.createObjectURL(r.data), mime: r.headers['content-type'] };
  },
  // Office プレビュー（Word/Excel/PowerPoint → PDF 変換）
  officePreviewState: (did) => axios.get(`${API}/documents/${did}/office-preview`).then((r) => r.data),
  officePreviewStart: (did, force) => axios.post(`${API}/documents/${did}/office-preview`,
    {}, { params: force ? { force: true } : {} }).then((r) => r.data),
  officePreviewPdfUrl: async (did) => {
    const r = await axios.get(`${API}/documents/${did}/office-preview/pdf`, { responseType: 'blob' });
    return { url: URL.createObjectURL(r.data) };
  },
  officeRender: (did, { sheet, maxRows, maxCols } = {}) =>
    axios.get(`${API}/documents/${did}/office-render`, {
      params: {
        ...(sheet ? { sheet } : {}),
        ...(maxRows ? { max_rows: maxRows } : {}),
        ...(maxCols ? { max_cols: maxCols } : {}),
      },
    }).then((r) => r.data),
  downloadFile: async (did, versionId, filename) => {
    const r = await axios.get(`${API}/documents/${did}/file`,
      { params: { download: true, ...(versionId ? { version_id: versionId } : {}) }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'document'; a.click();
    URL.revokeObjectURL(url);
  },
};

export const STATUS_STYLE = {
  draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  review: 'bg-sky-50 text-sky-700 border-sky-200',
  reviewed: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  released: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  obsolete: 'bg-amber-50 text-amber-700 border-amber-200',
  archived: 'bg-stone-100 text-stone-600 border-stone-300',
  needs_correction: 'bg-rose-50 text-rose-700 border-rose-200',
  needs_classification: 'bg-violet-50 text-violet-700 border-violet-200',
  needs_split: 'bg-orange-50 text-orange-700 border-orange-200',
};

// 2026-08: 利用者に見せる状態は 有効 / 旧版 / アーカイブ の3つだけ
export const SIMPLE_STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  obsolete: 'bg-amber-50 text-amber-700 border-amber-200',
  archived: 'bg-stone-100 text-stone-600 border-stone-300',
};
export const SIMPLE_STATUS_OPTIONS = [
  { value: '', label: 'すべての状態' },
  { value: 'active', label: '有効' },
  { value: 'obsolete', label: '旧版' },
  { value: 'archived', label: 'アーカイブ' },
];

export const fmtSize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  return formatDate(iso) || String(iso).slice(0, 10);
};

// L10 — 全角で読み取られた文字は、画面でも検索でも半角と同じに見せる。
// 保存してある原文は触らない（表示のときだけ字形をそろえる）。
export const nfkc = (s) => (s == null ? '' : String(s).normalize('NFKC'));
